import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ListingPageResult } from '../../domain/models/ProductListing';

export const PLP_INITIAL_SLICE_NAME = 'plpInitial';

export interface PlpInitialState {
  /** First-page listing data from SSR; used so client does not refetch page 1. */
  data: ListingPageResult | null;
  /**
   * The URL (`pathname + search`) the SSR data was fetched for. Used to ensure the
   * client only consumes this data when the current route matches; otherwise a
   * later client-side navigation (e.g. applying a filter) would reuse stale rows
   * keyed to the original landing URL.
   */
  url: string | null;
}

const initialState: PlpInitialState = {
  data: null,
  url: null,
};

interface SetPlpInitialDataPayload {
  data: ListingPageResult | null;
  url: string | null;
}

const plpInitialSlice = createSlice({
  name: PLP_INITIAL_SLICE_NAME,
  initialState,
  reducers: {
    setPlpInitialData(state, action: PayloadAction<SetPlpInitialDataPayload>) {
      state.data = action.payload.data ?? null;
      state.url = action.payload.url ?? null;
    },
    clearPlpInitialData(state) {
      state.data = null;
      state.url = null;
    },
  },
});

export const plpInitialReducer = plpInitialSlice.reducer;
export const { setPlpInitialData, clearPlpInitialData } = plpInitialSlice.actions;

/** Select initial PLP data from Redux (SSR first-page data). */
export function selectPlpInitialData(state: { [PLP_INITIAL_SLICE_NAME]?: PlpInitialState }): ListingPageResult | null {
  return state[PLP_INITIAL_SLICE_NAME]?.data ?? null;
}

/** Select the URL that the SSR PLP data corresponds to (or null when not seeded). */
export function selectPlpInitialUrl(state: { [PLP_INITIAL_SLICE_NAME]?: PlpInitialState }): string | null {
  return state[PLP_INITIAL_SLICE_NAME]?.url ?? null;
}
